// Timeline sampling (CREATIVE-MODEL-V1.md §7, RUNTIME-CONTRACT-V1.md §6, §13):
// the animated state of an element is a PURE function of (its tracks, its
// visibility window, the scene time). No clock, no randomness, no DOM. The
// Creative Studio playback (7.6D) and the Delivery Runtime (7.6H) use THIS
// function, so what is previewed is what is served (ADR-0020).
//
// Semantics fixed here (all within ADR-0018; documented in CREATIVE-MODEL §17.H):
//
// - A track's active window is [start.at + delay, + duration × iterations)
//   ("infinite" = until the scene ends). Manual tracks are sampled only from
//   the instant an action started them (`manualStarts`).
// - Easing applies PER SEGMENT between keyframes (CSS semantics): the easing
//   of the keyframe a segment starts from, else the track easing. A property
//   is interpolated between the keyframes that declare it; before the first /
//   after the last one it holds that keyframe's value.
// - `direction`: reverse plays every iteration backwards; alternate reverses
//   odd iterations (0-based). After the window the track holds its final value.
// - Composition per property: (1) among ACTIVE tracks the last in the list
//   wins (validator warns on overlaps); (2) else the track that FINISHED most
//   recently holds its final value; (3) else, before any track touched the
//   property, an upcoming ENTRANCE track holds its initial value (the element
//   waits "outside" until it enters); (4) else the element is at rest.
// - Values are relative to the element's rest presentation: offsets add to the
//   frame position, scales multiply, rotation adds to the frame rotation,
//   opacity multiplies the element opacity. Rest = { 0, 0, 1, 1, 0, 1 }.
// - The element's `timing` window [start, end) gates visibility; outside it the
//   element is not rendered (tracks keep their own clock: the scene time).
// - Spring easing is the closed-form damped oscillator from 0 to 1 over the
//   track duration in seconds, forced to exactly 1 at the end of the segment.

import type { AnimationTrack, Easing } from "./schema/document.js";

export const ANIMATABLE_PROPS = ["offsetX", "offsetY", "scaleX", "scaleY", "rotation", "opacity"] as const;
export type AnimatableProp = (typeof ANIMATABLE_PROPS)[number];

export interface AnimatedValues {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly rotation: number;
  readonly opacity: number;
}

export const REST_VALUES: AnimatedValues = { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 };

// ─── Easing ─────────────────────────────────────────────────────────────────

/** CSS definitions of the named easings. */
export const NAMED_EASING_BEZIER: Readonly<Record<string, readonly [number, number, number, number]>> = {
  ease: [0.25, 0.1, 0.25, 1],
  easeIn: [0.42, 0, 1, 1],
  easeOut: [0, 0, 0.58, 1],
  easeInOut: [0.42, 0, 0.58, 1],
};

function cubicBezier(x1: number, y1: number, x2: number, y2: number, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bx = (t: number) => ((1 - 3 * x2 + 3 * x1) * t + (3 * x2 - 6 * x1)) * t * t + 3 * x1 * t;
  const by = (t: number) => ((1 - 3 * y2 + 3 * y1) * t + (3 * y2 - 6 * y1)) * t * t + 3 * y1 * t;
  const dx = (t: number) => 3 * (1 - 3 * x2 + 3 * x1) * t * t + 2 * (3 * x2 - 6 * x1) * t + 3 * x1;
  // Newton iterations, then bisection as a deterministic fallback.
  let t = x;
  for (let i = 0; i < 8; i++) {
    const err = bx(t) - x;
    if (Math.abs(err) < 1e-7) return by(t);
    const d = dx(t);
    if (Math.abs(d) < 1e-6) break;
    t -= err / d;
  }
  let lo = 0;
  let hi = 1;
  t = x;
  for (let i = 0; i < 40; i++) {
    const v = bx(t);
    if (Math.abs(v - x) < 1e-7) break;
    if (v < x) lo = t;
    else hi = t;
    t = (lo + hi) / 2;
  }
  return by(t);
}

function springValue(spring: { stiffness: number; damping: number; mass: number }, seconds: number): number {
  const { stiffness: k, damping: c, mass: m } = spring;
  const w0 = Math.sqrt(k / m);
  const zeta = c / (2 * Math.sqrt(k * m));
  if (zeta < 1) {
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    const e = Math.exp(-zeta * w0 * seconds);
    return 1 - e * (Math.cos(wd * seconds) + ((zeta * w0) / wd) * Math.sin(wd * seconds));
  }
  if (zeta === 1) return 1 - Math.exp(-w0 * seconds) * (1 + w0 * seconds);
  const s = Math.sqrt(zeta * zeta - 1);
  const r1 = -w0 * (zeta - s);
  const r2 = -w0 * (zeta + s);
  const a = r2 / (r2 - r1);
  return 1 - a * Math.exp(r1 * seconds) - (1 - a) * Math.exp(r2 * seconds);
}

/** Eased progress of `p` ∈ [0,1]. `spanMs` (the segment length) is used only by spring easing. */
export function ease(easing: Easing, p: number, spanMs = 1_000): number {
  const x = Math.min(1, Math.max(0, p));
  if (easing === "linear") return x;
  if (typeof easing === "string") {
    const b = NAMED_EASING_BEZIER[easing];
    return b ? cubicBezier(b[0], b[1], b[2], b[3], x) : x;
  }
  if ("cubicBezier" in easing) {
    const [x1, y1, x2, y2] = easing.cubicBezier;
    return cubicBezier(x1, y1, x2, y2, x);
  }
  if ("steps" in easing) {
    const n = easing.steps;
    if (x >= 1) return 1;
    return easing.position === "start" ? Math.min(1, Math.floor(x * n + 1) / n) : Math.floor(x * n) / n;
  }
  if (x >= 1) return 1;
  return springValue(easing.spring, (x * spanMs) / 1_000);
}

// ─── One track ──────────────────────────────────────────────────────────────

const round = (v: number) => Math.round(v * 1e6) / 1e6;

/** Value of every property declared by the track's keyframes at local progress `p` ∈ [0,1]. */
export function sampleKeyframes(track: AnimationTrack, p: number): Partial<Record<AnimatableProp, number>> {
  const out: Partial<Record<AnimatableProp, number>> = {};
  const x = Math.min(1, Math.max(0, p));
  for (const prop of ANIMATABLE_PROPS) {
    const keys = track.keyframes.filter((k) => k.props[prop] !== undefined);
    const first = keys[0];
    const last = keys[keys.length - 1];
    if (!first || !last) continue;
    const value = (k: (typeof keys)[number]) => k.props[prop] as number;
    if (x <= first.at) out[prop] = value(first);
    else if (x >= last.at) out[prop] = value(last);
    else {
      for (let i = 1; i < keys.length; i++) {
        const a = keys[i - 1];
        const b = keys[i];
        if (!a || !b || x > b.at) continue;
        const local = (x - a.at) / (b.at - a.at);
        const e = ease(a.easing ?? track.easing, local, (b.at - a.at) * track.duration);
        out[prop] = round(value(a) + (value(b) - value(a)) * e);
        break;
      }
    }
  }
  return out;
}

export interface TrackWindow {
  readonly start: number;
  /** Exclusive end; Infinity for "infinite" iterations. */
  readonly end: number;
}

/** Active window of a timeline track (or of a manual track started at `manualStart`). */
export function trackWindow(track: AnimationTrack, manualStart?: number): TrackWindow | null {
  const base = track.start.mode === "timeline" ? track.start.at : manualStart;
  if (base === undefined) return null;
  const start = base + (track.delay ?? 0);
  const iterations = track.iterations ?? 1;
  return { start, end: iterations === "infinite" ? Number.POSITIVE_INFINITY : start + track.duration * iterations };
}

function directedProgress(track: AnimationTrack, iteration: number, p: number): number {
  const dir = track.direction ?? "normal";
  if (dir === "reverse") return 1 - p;
  if (dir === "alternate" && iteration % 2 === 1) return 1 - p;
  return p;
}

/** Keyframe progress (after direction) of a track at time `t`, or the phase it is in. */
export function trackProgressAt(
  track: AnimationTrack,
  t: number,
  manualStart?: number
): { readonly state: "before" | "active" | "after" | "idle"; readonly p: number } {
  const w = trackWindow(track, manualStart);
  if (!w) return { state: "idle", p: 0 };
  if (t < w.start) return { state: "before", p: directedProgress(track, 0, 0) };
  if (t >= w.end) {
    const n = track.iterations === "infinite" ? 1 : (track.iterations ?? 1);
    return { state: "after", p: directedProgress(track, n - 1, 1) };
  }
  const elapsed = t - w.start;
  const iteration = Math.floor(elapsed / track.duration);
  const local = (elapsed - iteration * track.duration) / track.duration;
  return { state: "active", p: directedProgress(track, iteration, local) };
}

// ─── Composition ────────────────────────────────────────────────────────────

export interface SampleOptions {
  /** Scene-time instant at which each manual track was started (preview/runtime `animation.play`). */
  readonly manualStarts?: Readonly<Record<string, number>>;
  /** Scene end (ms) used to stop "infinite" tracks; null = no end. */
  readonly sceneEnd?: number | null;
}

/** Composed animated values of one element's tracks at scene time `t` (see the rules above). */
export function sampleAnimations(
  tracks: readonly AnimationTrack[] | undefined,
  t: number,
  options: SampleOptions = {}
): AnimatedValues {
  if (!tracks || tracks.length === 0) return REST_VALUES;
  const out: Record<AnimatableProp, number> = { ...REST_VALUES };
  const states = tracks.map((track) => {
    const progress = trackProgressAt(track, t, options.manualStarts?.[track.id]);
    const w = trackWindow(track, options.manualStarts?.[track.id]);
    const end = w ? Math.min(w.end, options.sceneEnd ?? Number.POSITIVE_INFINITY) : 0;
    // An infinite track that reached the scene end holds the value it had there.
    const state = progress.state === "active" && t >= end ? "after" : progress.state;
    const p = state === "after" && w && w.end === Number.POSITIVE_INFINITY ? trackProgressAt(track, end).p : progress.p;
    return { track, state, p, start: w?.start ?? 0, end };
  });
  for (const prop of ANIMATABLE_PROPS) {
    const touching = states.filter((s) => s.track.keyframes.some((k) => k.props[prop] !== undefined));
    if (touching.length === 0) continue;
    const active = touching.filter((s) => s.state === "active");
    let chosen = active[active.length - 1];
    if (!chosen) {
      const done = touching.filter((s) => s.state === "after");
      // Latest end wins; ties: the later track in the list.
      chosen = done.reduce<(typeof done)[number] | undefined>(
        (best, s) => (!best || s.end >= best.end ? s : best),
        undefined
      );
    }
    if (!chosen) {
      const upcoming = touching
        .filter((s) => s.state === "before")
        .reduce<(typeof touching)[number] | undefined>(
          (best, s) => (!best || s.start < best.start ? s : best),
          undefined
        );
      if (upcoming?.track.phase === "entrance") chosen = upcoming;
    }
    if (!chosen) continue;
    const v = sampleKeyframes(chosen.track, chosen.p)[prop];
    if (v !== undefined) out[prop] = v;
  }
  return out;
}

/** Whether an element is inside its visibility window at scene time `t`. */
export function inTimingWindow(timing: { readonly start?: number; readonly end?: number } | undefined, t: number) {
  if (!timing) return true;
  if (timing.start !== undefined && t < timing.start) return false;
  if (timing.end !== undefined && t >= timing.end) return false;
  return true;
}

/** Last instant (ms) touched by an element's timeline tracks (Infinity if one repeats forever). */
export function tracksEnd(tracks: readonly AnimationTrack[] | undefined): number {
  let end = 0;
  for (const t of tracks ?? []) {
    const w = trackWindow(t);
    if (w) end = Math.max(end, w.end);
  }
  return end;
}
